import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, AwardIcon, TrendingUpIcon, UsersIcon, StarIcon, ZapIcon, TargetIcon, HeartIcon } from 'lucide-react';
export function VipPartnerProgram() {
  const benefits = [{
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3452" />,
    title: 'Elevate Your Event Brand',
    description: 'Stand out from competitors by offering world-class VIP experiences that attendees will talk about for years.'
  }, {
    icon: <TrendingUpIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3453" />,
    title: 'Increase Sponsorship Value',
    description: 'Create premium sponsorship tiers with exclusive access to VIP experiences, commanding higher rates.'
  }, {
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3454" />,
    title: 'Retain Top Attendees',
    description: 'Give your most valuable attendees compelling reasons to return year after year with unforgettable experiences.'
  }, {
    icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3455" />,
    title: 'Zero Event Planning Burden',
    description: 'We handle all logistics, planning, and execution while you focus on your core conference activities.'
  }, {
    icon: <ZapIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3456" />,
    title: 'Turn-Key Implementation',
    description: 'From concept to execution, we deliver fully managed VIP experiences that integrate seamlessly with your event.'
  }, {
    icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-3457" />,
    title: 'Strengthen Community Bonds',
    description: 'Create intimate networking opportunities for your top speakers, sponsors, and attendees in exclusive settings.'
  }];
  const experienceTypes = [{
    title: 'Pre-Conference VIP Retreat',
    description: 'Exclusive 2-3 day beach retreat before your main event for speakers, sponsors, and top attendees.',
    features: ['Private beachfront venue', 'Executive networking sessions', 'Luxury accommodations', 'Curated activities and experiences', 'Professional facilitation'],
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Post-Conference Wind-Down',
    description: 'Reward your VIPs with a relaxing post-event experience to decompress and deepen connections.',
    features: ['Wellness and recovery focus', 'Sunset yacht experiences', 'Spa and relaxation', 'Casual networking opportunities', 'Seamless transition from conference'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Mid-Conference VIP Day',
    description: 'Break up your multi-day conference with an exclusive day experience for top-tier attendees.',
    features: ['Day trips to unique locations', 'Team building activities', 'Private dining experiences', 'Cultural immersion', 'Photography and content creation'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  const howItWorks = [{
    step: '1',
    title: 'Discovery Call',
    description: 'We learn about your conference, attendee profile, and VIP experience goals.'
  }, {
    step: '2',
    title: 'Custom Proposal',
    description: 'Receive a tailored VIP experience package designed specifically for your event.'
  }, {
    step: '3',
    title: 'Seamless Integration',
    description: 'We coordinate with your team to integrate the VIP experience into your event schedule.'
  }, {
    step: '4',
    title: 'White-Glove Execution',
    description: 'Our team handles every detail while you receive regular updates and peace of mind.'
  }];
  const idealFor = ['Technology conferences with 500+ attendees', 'Industry trade shows and exhibitions', 'Professional association annual meetings', 'Executive summits and leadership forums', 'Innovation and startup conferences', 'International business conferences'];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-3458">
      {/* Hero */}
      <section className="relative bg-[#0099A1] py-24 overflow-hidden" data-id="element-3459">
        <div className="absolute inset-0 opacity-10" data-id="element-3460">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-3461">
            <defs data-id="element-3462">
              <pattern id="wave" patternUnits="userSpaceOnUse" width="100" height="20" patternTransform="rotate(45)" data-id="element-3463">
                <path d="M0 10 Q 12.5 0, 25 10 T 50 10 T 75 10 T 100 10" fill="none" stroke="white" strokeWidth="2" data-id="element-3464" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave)" data-id="element-3465" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3466">
          <div className="max-w-4xl mx-auto text-center" data-id="element-3467">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-6" data-id="element-3468">
              <TargetIcon className="h-4 w-4 mr-2" data-id="element-3469" />
              For Conference & Exhibition Organizers
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-3470">
              VIP Experience Partner Program
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8" data-id="element-3471">
              Transform Your Conference with Exclusive Beach Experiences That
              Your Most Valuable Attendees Will Never Forget
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3472">
              <Button variant="primary" size="lg" to="/contact" data-id="element-3473">
                Schedule Discovery Call
              </Button>
              <Button variant="outline" size="lg" className="bg-white/10 border-white text-white hover:bg-white/20" data-id="element-3474">
                Download Program Overview
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Problem/Solution */}
      <section className="py-16 bg-white" data-id="element-3475">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3476">
          <div className="max-w-4xl mx-auto" data-id="element-3477">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-3478">
              <div data-id="element-3479">
                <h2 className="text-3xl font-bold text-[#374047] mb-6" data-id="element-3480">
                  The Challenge Conference Organizers Face
                </h2>
                <div className="space-y-4" data-id="element-3481">
                  <div className="flex items-start" data-id="element-3482">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-3483"></div>
                    <p className="text-[#374047]/80" data-id="element-3484">
                      Sponsors demanding more value and unique activation
                      opportunities
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3485">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-3486"></div>
                    <p className="text-[#374047]/80" data-id="element-3487">
                      Top speakers and attendees expecting premium experiences
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3488">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-3489"></div>
                    <p className="text-[#374047]/80" data-id="element-3490">
                      Difficulty differentiating your event from competitors
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3491">
                    <div className="w-2 h-2 bg-[#FF6B65] rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-3492"></div>
                    <p className="text-[#374047]/80" data-id="element-3493">
                      Limited time and resources to plan additional VIP
                      experiences
                    </p>
                  </div>
                </div>
              </div>
              <div data-id="element-3494">
                <h2 className="text-3xl font-bold text-[#374047] mb-6" data-id="element-3495">
                  The DestaMeet Solution
                </h2>
                <div className="space-y-4" data-id="element-3496">
                  <div className="flex items-start" data-id="element-3497">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-3498" />
                    <p className="text-[#374047]/80" data-id="element-3499">
                      Turn-key VIP experiences that require zero effort from
                      your team
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3500">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-3501" />
                    <p className="text-[#374047]/80" data-id="element-3502">
                      Premium add-ons that increase event revenue and
                      sponsorship value
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3503">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-3504" />
                    <p className="text-[#374047]/80" data-id="element-3505">
                      Unforgettable experiences that boost attendee retention
                      and word-of-mouth
                    </p>
                  </div>
                  <div className="flex items-start" data-id="element-3506">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] mt-1 mr-3 flex-shrink-0" data-id="element-3507" />
                    <p className="text-[#374047]/80" data-id="element-3508">
                      Professional execution by destination management experts
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Benefits */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-3509">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3510">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3511">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3512">
              Why Partner with DestaMeet?
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-3513">
              We handle everything so you can focus on delivering an exceptional
              core conference experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3514">
            {benefits.map((benefit, index) => <Card key={index} className="h-full" data-id="element-3515">
                <CardBody className="p-6" data-id="element-3516">
                  <div className="mb-4" data-id="element-3517">{benefit.icon}</div>
                  <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-3518">
                    {benefit.title}
                  </h3>
                  <p className="text-[#374047]/80" data-id="element-3519">{benefit.description}</p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>
      {/* Experience Types */}
      <section className="py-16 bg-white" data-id="element-3520">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3521">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3522">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3523">
              VIP Experience Options
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-3524">
              Flexible formats designed to complement your conference schedule
              and objectives.
            </p>
          </div>
          <div className="space-y-16" data-id="element-3525">
            {experienceTypes.map((experience, index) => <div key={index} className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-3526">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-3527">
                  <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-3528">
                    {experience.title}
                  </h3>
                  <p className="text-lg text-[#374047]/80 mb-6" data-id="element-3529">
                    {experience.description}
                  </p>
                  <ul className="space-y-3" data-id="element-3530">
                    {experience.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-3531">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-3532" />
                        <span className="ml-3 text-[#374047]" data-id="element-3533">{feature}</span>
                      </li>)}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-3534">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-3535">
                    <img src={experience.image} alt={experience.title} className="w-full h-full object-cover" data-id="element-3536" />
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-3537">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3538">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3539">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-3540">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-3541">
              From first conversation to flawless execution, we make it easy.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto" data-id="element-3542">
            {howItWorks.map((item, index) => <div key={index} className="text-center" data-id="element-3543">
                <div className="w-16 h-16 bg-[#0099A1] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-3544">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-3545">
                  {item.title}
                </h3>
                <p className="text-[#374047]/80" data-id="element-3546">{item.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Ideal For */}
      <section className="py-16 bg-white" data-id="element-3547">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3548">
          <div className="max-w-4xl mx-auto" data-id="element-3549">
            <h2 className="text-3xl font-bold text-[#374047] mb-8 text-center" data-id="element-3550">
              Ideal For
            </h2>
            <div className="grid md:grid-cols-2 gap-4" data-id="element-3551">
              {idealFor.map((item, index) => <div key={index} className="flex items-center p-4 bg-[#F4E8D0]/30 rounded-lg" data-id="element-3552">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-3553" />
                  <span className="text-[#374047]" data-id="element-3554">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-3555">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3556">
          <div className="max-w-4xl mx-auto" data-id="element-3557">
            <h2 className="text-3xl font-bold text-[#374047] mb-12 text-center" data-id="element-3558">
              What Conference Organizers Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-3559">
              <Card data-id="element-3560">
                <CardBody className="p-6" data-id="element-3561">
                  <div className="flex mb-4" data-id="element-3562">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-[#FFB800] fill-current" data-id="element-3563" />)}
                  </div>
                  <p className="text-[#374047]/80 mb-4" data-id="element-3564">
                    "The VIP beach experience we added through DestaMeet became
                    the most talked-about part of our conference. Our sponsors
                    were thrilled with the exclusive access, and attendee
                    satisfaction scores went through the roof."
                  </p>
                  <div className="flex items-center" data-id="element-3565">
                    <div data-id="element-3566">
                      <p className="font-semibold text-[#374047]" data-id="element-3567">
                        Sarah Mitchell
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-3568">
                        Director, TechSummit Global
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card data-id="element-3569">
                <CardBody className="p-6" data-id="element-3570">
                  <div className="flex mb-4" data-id="element-3571">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-[#FFB800] fill-current" data-id="element-3572" />)}
                  </div>
                  <p className="text-[#374047]/80 mb-4" data-id="element-3573">
                    "Working with DestaMeet was effortless. They handled
                    everything from logistics to execution, allowing us to focus
                    on our core conference. The post-event feedback from our VIP
                    attendees was overwhelmingly positive."
                  </p>
                  <div className="flex items-center" data-id="element-3574">
                    <div data-id="element-3575">
                      <p className="font-semibold text-[#374047]" data-id="element-3576">
                        Michael Chen
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-3577">
                        VP Events, Innovation Forum
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 bg-[#0099A1] relative overflow-hidden" data-id="element-3578">
        <div className="absolute inset-0 opacity-10" data-id="element-3579">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-3580">
            <defs data-id="element-3581">
              <pattern id="wave-cta" patternUnits="userSpaceOnUse" width="100" height="20" patternTransform="rotate(45)" data-id="element-3582">
                <path d="M0 10 Q 12.5 0, 25 10 T 50 10 T 75 10 T 100 10" fill="none" stroke="white" strokeWidth="2" data-id="element-3583" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-cta)" data-id="element-3584" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3585">
          <div className="max-w-3xl mx-auto text-center" data-id="element-3586">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-3587">
              Ready to Elevate Your Conference?
            </h2>
            <p className="text-xl text-white/80 mb-8" data-id="element-3588">
              Let's discuss how we can create an unforgettable VIP experience
              for your most valuable attendees.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3589">
              <Button variant="primary" size="lg" to="/contact" data-id="element-3590">
                Schedule Discovery Call
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" href="https://wa.me/14154170322" data-id="element-3591">
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}